import React, { useMemo } from "react";

import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import { Stack, Typography } from "@mui/material";
import { BinDeleteIcon, CustomizeTable, PortfolioAnalysisIcon } from "assets/material-custom-icons";
import { ProgressOverlay, RoleComponentGuard } from "components";
import { AIAnswerText } from "components/Documents/AI-answer-text/AIAnswerText";
import { AIQuestionText } from "components/Documents/AI-question-text/AIQuestionText";
import { Menu } from "components/popups";
import { defaultDocumentReportQueries } from "constants/documents";
import { useActionHook, useModals } from "hooks";
import { usePrompt } from "hooks";
import { DocumentReportQueries, DocumentSectionGraph } from "store/types";

import { RoleName } from "../../../../constants";
import { useContentContext } from "../../Content/content-context";
import { EditGraphs } from "../../Report-sections-forms/EditGraphs";
import { EditQueries } from "../../Report-sections-forms/EditQueries";
import { ReportQueries } from "../ReportQueries";
import { Graph } from "./Graph";

interface Props {
  section: DocumentSectionGraph;
}

export const GraphSection: React.FC<Props> = ({ section }) => {
  const openPrompt = usePrompt();
  const { report_queries: globalQueries, deleteSection, updateSection } = useContentContext();
  const { errorModal } = useModals();

  const { action: updateSectionAction, loading: updateSectionLoading } = useActionHook(updateSection);

  const { settings, queries } = section.report_data;

  const resultQueries: DocumentReportQueries = useMemo(() => {
    return {
      ...defaultDocumentReportQueries,
      ...(queries || globalQueries)
    };
  }, [queries, globalQueries]);

  const updateReportData = async (data: DocumentSectionGraph["report_data"]) => {
    try {
      await updateSectionAction({
        id: section.id,
        report_data: data
      });
    } catch (_error) {
      errorModal({ msg: "Failed to update graph section" });
    }
  };

  const updateSettings = async () => {
    const updatedSettings = await openPrompt({
      Component: EditGraphs,
      props: {
        data: settings
      }
    });

    if (!updatedSettings) return;

    updateReportData({
      settings: updatedSettings,
      ...(queries && { queries })
    });
  };

  const updateQueries = async () => {
    const updateQueries = await openPrompt({
      Component: EditQueries,
      props: { data: resultQueries }
    });

    if (!updateQueries) return;

    updateReportData({
      queries: updateQueries,
      settings: settings
    });
  };

  return (
    <Stack gap="10px">
      {section.parameters?.question && <AIQuestionText question={section.parameters?.question} />}
      {section.parameters?.question && section.text && (
        <Stack flexDirection="row" gap="5px" alignItems="center" justifyContent="flex-start">
          <PortfolioAnalysisIcon sx={{ fill: "#1C1B1F", width: "15px", height: "15px" }} />
          <Typography sx={{ fontWeight: 400, fontSize: "14px", color: "#071F35" }}>Graph</Typography>
        </Stack>
      )}
      {section.text && <AIAnswerText answer={section.text} />}
      <ProgressOverlay
        inProgress={updateSectionLoading}
        containerSX={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          gap: "8px",
          bgcolor: "white",
          borderRadius: "12px",
          boxShadow: "0px 1px 4px 0px #1212171F",
          p: "16px",
          "&:hover": {
            "& .graph-section-menu": {
              opacity: 1
            }
          }
        }}
      >
        <Stack sx={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontWeight: 500, fontSize: "18px", lineHeight: "24px", color: "#071F35" }}>
            Graphs
          </Typography>
          <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
            <Menu
              keepTriggerEnabled
              iconButtonProps={{
                sx: { opacity: 0, transition: "opacity .15s linear" },
                size: "sm",
                className: "graph-section-menu"
              }}
              options={[
                { title: "Customize graphs", onClick: updateSettings, icon: <CustomizeTable /> },
                {
                  title: "Parameter set",
                  icon: <ViewWeekOutlinedIcon />,
                  onClick: updateQueries
                },
                {
                  title: "Delete",
                  icon: <BinDeleteIcon />,
                  onClick: () => deleteSection({ sectionId: section.id })
                }
              ]}
            />
          </RoleComponentGuard>
        </Stack>
        {section.report_data.queries && <ReportQueries {...section.report_data.queries} />}
        <Stack
          sx={{
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "space-between"
          }}
        >
          {settings.map((item, index) => (
            <Graph
              key={index}
              settings={item}
              queries={resultQueries}
              documentId={section.document_id}
              sectionId={section.id}
              index={index}
              totalGraphs={settings.length}
            />
          ))}
        </Stack>
      </ProgressOverlay>
    </Stack>
  );
};
